import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, MailCheck, XCircle } from "lucide-react";
import { AuthShell, PrimaryButton, inputCls } from "@/components/AuthShell";
import { api } from "@/lib/api";

const US_STATES = "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
const HEAR = ["Google", "Facebook", "Instagram", "TikTok", "LinkedIn", "YouTube", "Referral", "Trade show", "Other"];

const Field = ({ label, children }) => (
  <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-[0.2em] text-[#4CCC06]">{label}</span>{children}</label>
);

const CheckEmail = ({ email, onResend, resendMsg }) => (
  <div className="text-center" data-testid="signup-check-email">
    <MailCheck size={56} className="mx-auto text-[#4CCC06]" />
    <h1 className="mt-3 font-display text-3xl font-extrabold uppercase">Check your email</h1>
    <p className="mt-2 text-sm text-neutral-400">We sent a verification link to <b className="text-white">{email}</b>. Click it to activate your account, then sign in.</p>
    {onResend && <button onClick={onResend} data-testid="signup-resend" className="mt-4 text-sm font-bold text-[#4CCC06] underline">Resend the email</button>}
    {resendMsg && <p className="mt-2 text-xs text-neutral-400">{resendMsg}</p>}
    <Link to="/" className="mt-6 block text-sm font-bold text-neutral-400 underline">Back to sign in</Link>
  </div>
);

export function SignupRental() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ full_name: "", email: "", password: "", company: "", phone: "", equipment_renting: "", street: "", city: "", state: "", zip: "", facility_code: params.get("code") || "" });
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const raw = f.facility_code.trim();
    if (raw.length < 4) { setCode(null); return; }
    const t = setTimeout(() => api(`/rental-facility-code/validate?code=${encodeURIComponent(raw)}`, { auth: false }).then(setCode).catch(() => setCode({ valid: false })), 500);
    return () => clearTimeout(t);
  }, [f.facility_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.full_name.trim().length < 2) return setErr("Enter your full name.");
    if (f.password.length < 8) return setErr("Password must be at least 8 characters.");
    setBusy(true); setErr(null);
    try {
      const r = await api("/auth/register-rental", { method: "POST", auth: false, body: {
        full_name: f.full_name.trim(), email: f.email.trim().toLowerCase(), password: f.password, company: f.company.trim(), phone: f.phone.trim(),
        equipment_renting: f.equipment_renting.trim(), address: { street: f.street, city: f.city, state: f.state, zip: f.zip }, facility_code: f.facility_code.trim() || null,
      } });
      setDone(r);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };

  if (done) return <AuthShell><CheckEmail email={done.email} />{done.lso_fee_waived && <p className="mt-4 text-center text-sm text-[#4CCC06]" data-testid="signup-fee-waived">Facility code applied — your $30 LSO training fee is waived.</p>}</AuthShell>;
  return (
    <AuthShell>
      <p className="text-[11px] font-bold tracking-[0.3em] text-[#FFD400]">RENTAL CUSTOMER</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold uppercase">Create your rental account</h1>
      <p className="mt-1 text-sm text-neutral-400">Sign up in 60 seconds · Verify email · $30 LSO Operator Safety Training</p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-rental-form">
        <Field label="FULL NAME *"><input data-testid="sr-name" className={inputCls} value={f.full_name} onChange={set("full_name")} /></Field>
        <Field label="EMAIL *"><input data-testid="sr-email" type="email" required className={inputCls} value={f.email} onChange={set("email")} /></Field>
        <Field label="PASSWORD *"><input data-testid="sr-password" type="password" className={inputCls} value={f.password} onChange={set("password")} placeholder="Min. 8 characters" /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="COMPANY"><input data-testid="sr-company" className={inputCls} value={f.company} onChange={set("company")} /></Field>
          <Field label="PHONE"><input data-testid="sr-phone" type="tel" className={inputCls} value={f.phone} onChange={set("phone")} /></Field>
        </div>
        <Field label="EQUIPMENT YOU'RE RENTING"><input data-testid="sr-equipment" className={inputCls} value={f.equipment_renting} onChange={set("equipment_renting")} placeholder="e.g. 1000W fiber laser" /></Field>
        <Field label="ADDRESS"><input data-testid="sr-street" className={inputCls} value={f.street} onChange={set("street")} placeholder="Street" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <input data-testid="sr-city" className={inputCls} value={f.city} onChange={set("city")} placeholder="City" />
          <input data-testid="sr-state" className={inputCls} value={f.state} onChange={set("state")} placeholder="State" />
          <input data-testid="sr-zip" className={inputCls} value={f.zip} onChange={set("zip")} placeholder="ZIP" />
        </div>
        <Field label="FACILITY CODE (OPTIONAL)">
          <input data-testid="sr-facility-code" className={inputCls} value={f.facility_code} onChange={set("facility_code")} placeholder="From your rental facility" />
          {code && <p className={`mt-1 text-xs ${code.valid ? "text-[#4CCC06]" : "text-[#FF6B6B]"}`} data-testid="sr-code-status">{code.valid ? `Valid — ${code.label || "LSO fee waived"}` : "Code not recognized — you can still sign up."}</p>}
        </Field>
        {err && <p className="text-[13px] text-[#FF3333]" data-testid="signup-error-banner">{err}</p>}
        <PrimaryButton type="submit" disabled={busy} data-testid="signup-submit">{busy ? <Loader2 className="animate-spin" size={18} /> : "CREATE ACCOUNT"}</PrimaryButton>
      </form>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "busy" : "missing");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!token) return;
    api("/auth/verify-email-token", { method: "POST", auth: false, body: { token } })
      .then(() => setState("ok")).catch((e) => { setMsg(e.message); setState("fail"); });
  }, [token]);
  return (
    <AuthShell>
      <div className="py-4 text-center" data-testid={`verify-${state}`}>
        {state === "busy" && <Loader2 className="mx-auto animate-spin text-[#4CCC06]" />}
        {state === "ok" && <><CheckCircle2 size={56} className="mx-auto text-[#4CCC06]" /><h1 className="mt-3 font-display text-3xl font-extrabold uppercase">Email verified</h1><p className="mt-2 text-sm text-neutral-400">Your account is active. Sign in to continue.</p></>}
        {(state === "fail" || state === "missing") && <><XCircle size={56} className="mx-auto text-[#FF3333]" /><h1 className="mt-3 font-display text-3xl font-extrabold uppercase">Link not valid</h1><p className="mt-2 text-sm text-neutral-400">{msg || "This verification link is missing or expired."}</p></>}
        {state !== "busy" && <PrimaryButton onClick={() => navigate("/")} data-testid="verify-signin">GO TO SIGN IN</PrimaryButton>}
      </div>
    </AuthShell>
  );
}
